/**
 * Url and header helpers shared by the pending-request tracking and the lifecycle interceptors: which pathname
 * telemetry may see, which template labels it, and the Seeq request id header. `resolveOrigin` is the only thing in the
 * package that reads `globalThis.location`.
 */

import { SEEQ_REQUEST_ID_HEADER } from './axios.constants';
import type { AxiosRequestHeaders } from 'axios';

/** Urls with no origin of their own resolve against this base. Nothing is ever sent to http://relative.invalid. */
const RELATIVE_URL_BASE = 'http://relative.invalid';

/**
 * The pathname telemetry should report for a request, or `undefined` if it should not be reported.
 *
 * `uri` is the url axios built (`instance.getUri(config)`), so baseURL handling is axios's, not ours. `origin` comes
 * from `resolveOrigin` and is already a plain origin, so it is compared as-is. Relative urls are always reported;
 * absolute ones only when they are on `origin`. A missing `uri`, or one `URL` cannot parse, gives `undefined`.
 */
export function parsePathname(uri: string | undefined, origin?: string): string | undefined {
  if (uri === undefined) {
    return undefined;
  }

  const base = origin || RELATIVE_URL_BASE;
  try {
    const url = new URL(uri, base);
    return url.origin === base ? url.pathname : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The origin a base url points at, for telemetry to compare request urls against.
 *
 * An absolute `baseUrl` like `https://example.com/api/` gives its own origin. A path like `/api` has none of its own,
 * so it takes the page's. Returns `undefined` when there is no origin to be had: no `baseUrl`, a path-only one with no
 * page to resolve it against, an unparseable one, or one that is itself opaque such as a `data:` url.
 */
export function resolveOrigin(baseUrl?: string): string | undefined {
  if (!baseUrl) {
    return undefined;
  }

  const origin = globalThis.location?.origin;
  // An opaque origin, from a sandboxed frame or a `data:` document, serializes to the string 'null'.
  const page = !origin || origin === 'null' ? undefined : origin;

  try {
    // With no page, a path-only `baseUrl` has nothing to resolve against and `new URL` throws.
    const { origin } = page ? new URL(baseUrl, page) : new URL(baseUrl);
    return origin === 'null' ? undefined : origin;
  } catch {
    return undefined;
  }
}

/**
 * The `url.template` used when a request did not declare one, which means a hand-written axios call rather than a
 * generated SDK one. A fixed string rather than the real path, so a one-off url can never turn into a new metric label.
 * Set `urlTemplate` on the request config to name the endpoint instead.
 */
export const UNTEMPLATED_URL_TEMPLATE = '{untemplated}';

// `new URL` percent-encodes the braces in a `{param}` placeholder; this puts them back. Placeholder names are always
// identifiers, so nothing else gets decoded.
const ENCODED_BRACE = /%7B(\w+)%7D/gi;

/**
 * The `url.template` for a request: the declared template's pathname, else the constant untemplated label.
 */
export function resolveUrlTemplate(urlTemplate?: string): string {
  try {
    return urlTemplate
      ? new URL(urlTemplate, RELATIVE_URL_BASE).pathname.replace(ENCODED_BRACE, '{$1}')
      : UNTEMPLATED_URL_TEMPLATE;
  } catch {
    return UNTEMPLATED_URL_TEMPLATE;
  }
}

/** Read the Seeq request id header, whether headers are an `AxiosHeaders` instance or a plain object. */
export function getRequestIdFromHeaders(headers?: AxiosRequestHeaders): string | undefined {
  if (!headers) {
    return undefined;
  }
  if (typeof headers.get === 'function') {
    const value = headers.get(SEEQ_REQUEST_ID_HEADER);
    return typeof value === 'string' ? value : undefined;
  }

  const value = headers[SEEQ_REQUEST_ID_HEADER];
  return typeof value === 'string' ? value : undefined;
}
